import { useAuth } from '../../contexts/AuthContext';
import { LogOut, Moon, Sun, User } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

function Header() {
  const { user, logout } = useAuth();
  const [darkMode, setDarkMode] = useState(
    document.documentElement.classList.contains('dark')
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
    localStorage.setItem('darkMode', !darkMode);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  return (
    // No backdrop-blur here, and the background is fully opaque on purpose.
    // backdrop-filter makes an element a "backdrop root", which Chrome
    // composites into its own layer — that layer kept painting over the modal
    // dim even though the dim is z-60 and this header is z-40, leaving a lit
    // 48px band across the top of every popup. The blur bought nothing at 90%
    // opacity anyway.
    <header className="h-12 bg-surface border-b border-line fixed top-0 right-0 left-0 z-40 shadow-sm">
      <div className="flex items-center justify-end h-full px-4 gap-2">
        {/* Dark mode toggle */}
        <button
          onClick={toggleDarkMode}
          className="p-1.5 rounded-lg hover:bg-surface-muted transition-colors text-ink-muted hover:text-ink"
        >
          {darkMode ? (
            <Sun className="w-4 h-4 text-amber-500" />
          ) : (
            <Moon className="w-4 h-4" />
          )}
        </button>
        
        {/* Divider */}
        <div className="w-px h-6 bg-line" />
        
        {/* User profile with dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={toggleDropdown}
            className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105"
          >
            <User className="w-4 h-4 text-white" />
          </button>
          
          {/* Dropdown menu */}
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-56 rounded-[4px] overflow-hidden menu-panel animate-in fade-in slide-in-from-top-2 duration-200">
              {/* User info */}
              <div 
                className="px-4 py-3 border-b border-line hover:bg-surface-muted transition-colors group cursor-default"
                title={user?.role || 'admin'}
              >
                <p className="text-sm font-semibold text-ink">
                  {user?.displayName || 'User'}
                </p>
                <p className="text-xs text-ink-muted mt-0.5 group-hover:text-ink transition-colors">
                  {user?.email || ''}
                </p>
              </div>
              
              {/* Logout button */}
              <button
                onClick={() => {
                  setShowDropdown(false);
                  logout();
                }}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
